import type {ReactNode} from 'react'
import {Component, use} from 'react'
import {AnalyticsContext} from '@github-ui/analytics-provider/context'

export type PreloadedQueryBoundaryProps = {
  children: ReactNode
  onRetry: () => void
  fallback?: (retry: () => void, error: Error) => ReactNode
  critical?: boolean
  appName?: string
}

type State = {
  error: Error | null
}

const defaultFallBack = (retry: () => void) => {
  return (
    <div>
      <span>Error:</span>
      <button onClick={retry}>Retry</button>
    </div>
  )
}

export type ReportableError = Error & {shouldSkipReport?: boolean}

/**
 * Marker symbol to indicate an error should not be reported to Sentry.
 * Used by React 19's onCaughtError callback to skip reporting.
 */
export const SKIP_ERROR_REPORT = Symbol('skipErrorReport')

export class BasicPreloadedQueryBoundary extends Component<PreloadedQueryBoundaryProps, State> {
  override state = {error: null}

  static getDerivedStateFromError(error: Error): State {
    return {error}
  }

  _retry = () => {
    // This ends up calling loadQuery again to get and render
    // a new query reference
    this.props.onRetry()
    this.setState({
      // Clear the error
      error: null,
    })
  }

  override render() {
    const {children} = this.props
    const {error} = this.state
    const fallback = this.props.fallback || defaultFallBack

    if (error) {
      return fallback(this._retry, error)
    }

    return children
  }
}

export default function PreloadedQueryBoundary(props: PreloadedQueryBoundaryProps) {
  const context = use(AnalyticsContext)
  const appName = props.appName || context?.appName
  return <BasicPreloadedQueryBoundary {...props} appName={appName} />
}

try{ SKIP_ERROR_REPORT.displayName ||= 'SKIP_ERROR_REPORT' } catch {}
try{ PreloadedQueryBoundary.displayName ||= 'PreloadedQueryBoundary' } catch {}